import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { Building, Users, Link as LinkIcon, Target, CheckCircle, ArrowRight, ArrowLeft, Loader2, Facebook, Instagram, Linkedin, Twitter } from 'lucide-react';
import { cn } from '../lib/utils';

const steps = [
  { id: 1, title: 'Business Profile', icon: Building },
  { id: 2, title: 'Target Audience', icon: Users },
  { id: 3, title: 'Connect Accounts', icon: LinkIcon },
  { id: 4, title: 'Goals & Strategy', icon: Target },
  { id: 5, title: 'Review & Launch', icon: CheckCircle },
];

const socialPlatforms = [
  { id: 'facebook', name: 'Facebook', icon: Facebook, color: '#1877F2' },
  { id: 'instagram', name: 'Instagram', icon: Instagram, color: '#E4405F' },
  { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: '#0A66C2' },
  { id: 'twitter', name: 'X/Twitter', icon: Twitter, color: '#000000' },
];

const goals = [
  'Brand Awareness',
  'Lead Generation',
  'Sales & Revenue',
  'Reputation Building',
  'Customer Engagement',
  'Website Traffic',
];

export const OnboardingPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    business_profile: { business_name: '', website_url: '', industry: '', brand_colors: '#10B981' },
    target_audience: { location: '', demographics: '', persona: '' },
    connected_accounts: {},
    goals_strategy: { goals: [], budget_range: '' },
  });
  const { completeOnboarding } = useAuth();
  const navigate = useNavigate();

  const updateData = (section, field, value) => {
    setData(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const toggleGoal = (goal) => {
    setData(prev => ({
      ...prev,
      goals_strategy: {
        ...prev.goals_strategy,
        goals: prev.goals_strategy.goals.includes(goal)
          ? prev.goals_strategy.goals.filter(g => g !== goal)
          : [...prev.goals_strategy.goals, goal]
      }
    }));
  };

  const handleNext = () => {
    if (currentStep < 5) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSkip = () => {
    handleNext();
  };

  const handleComplete = async () => {
    setLoading(true);
    const result = await completeOnboarding(data);
    if (result.success) {
      navigate('/dashboard');
    }
    setLoading(false);
  };

  const progress = (currentStep / 5) * 100;

  return (
    <div className="min-h-screen bg-off-white py-8 px-4" data-testid="onboarding-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src="/konnect-logo.png" alt="Konnect" className="h-10 w-auto object-contain" />
          </div>
          <h1 className="text-2xl font-heading font-bold text-text-primary mb-2">Let's Set Up Your Account</h1>
          <p className="text-gray-medium">Complete these steps to get the most out of Konnect — your unified business platform</p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <Progress value={progress} className="h-2 mb-4" />
          <div className="flex justify-between">
            {steps.map((step) => {
              const Icon = step.icon;
              const isActive = step.id === currentStep;
              const isComplete = step.id < currentStep;
              return (
                <div key={step.id} className="flex flex-col items-center">
                  <div className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center mb-1 transition-colors",
                    isActive ? "bg-emerald-brand text-white" :
                    isComplete ? "bg-emerald-brand/20 text-emerald-brand" :
                    "bg-gray-200 text-gray-medium"
                  )}>
                    {isComplete ? <CheckCircle className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                  </div>
                  <span className={cn(
                    "text-xs hidden sm:block",
                    isActive ? "text-emerald-brand font-medium" : "text-gray-medium"
                  )}>
                    {step.title}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <Card className="shadow-lg">
          <CardContent className="p-6">
            {/* Step 1: Business Profile */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <h2 className="text-xl font-heading font-semibold text-text-primary mb-4">Business Profile</h2>
                <div>
                  <Label htmlFor="business_name">Business Name</Label>
                  <Input
                    id="business_name"
                    value={data.business_profile.business_name}
                    onChange={(e) => updateData('business_profile', 'business_name', e.target.value)}
                    placeholder="Acme Inc."
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="website_url">Website URL</Label>
                  <Input
                    id="website_url"
                    value={data.business_profile.website_url}
                    onChange={(e) => updateData('business_profile', 'website_url', e.target.value)}
                    placeholder="https://example.com"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="industry">Industry</Label>
                  <Input
                    id="industry"
                    value={data.business_profile.industry}
                    onChange={(e) => updateData('business_profile', 'industry', e.target.value)}
                    placeholder="e.g., Retail, Healthcare, Professional Services"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>Brand Color</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="color"
                      value={data.business_profile.brand_colors}
                      onChange={(e) => updateData('business_profile', 'brand_colors', e.target.value)}
                      className="w-10 h-10 rounded border cursor-pointer"
                    />
                    <Input
                      value={data.business_profile.brand_colors}
                      onChange={(e) => updateData('business_profile', 'brand_colors', e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Target Audience */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <h2 className="text-xl font-heading font-semibold text-text-primary mb-4">Target Audience</h2>
                <div>
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={data.target_audience.location}
                    onChange={(e) => updateData('target_audience', 'location', e.target.value)}
                    placeholder="e.g., United States, California, New York City"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="demographics">Demographics</Label>
                  <Input
                    id="demographics"
                    value={data.target_audience.demographics}
                    onChange={(e) => updateData('target_audience', 'demographics', e.target.value)}
                    placeholder="e.g., Age 25-45, Homeowners, Small Business Owners"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="persona">Customer Persona</Label>
                  <Textarea
                    id="persona"
                    value={data.target_audience.persona}
                    onChange={(e) => updateData('target_audience', 'persona', e.target.value)}
                    placeholder="Describe your ideal customer..."
                    className="mt-2 min-h-[100px]"
                  />
                </div>
              </div>
            )}

            {/* Step 3: Connect Accounts */}
            {currentStep === 3 && (
              <div className="space-y-4">
                <h2 className="text-xl font-heading font-semibold text-text-primary mb-4">Connect Social Accounts</h2>
                <p className="text-gray-medium text-sm mb-4">Connect your social media accounts to enable automated posting and analytics.</p>
                <div className="grid grid-cols-2 gap-4">
                  {socialPlatforms.map((platform) => {
                    const Icon = platform.icon;
                    const isConnected = data.connected_accounts[platform.id];
                    return (
                      <div
                        key={platform.id}
                        className={cn(
                          "flex items-center justify-between p-4 border-2 rounded-xl transition-all",
                          isConnected ? "border-emerald-brand bg-emerald-brand/5" : "border-gray-border"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-xl flex items-center justify-center"
                            style={{ backgroundColor: `${platform.color}15` }}
                          >
                            <Icon className="h-5 w-5" style={{ color: platform.color }} />
                          </div>
                          <span className="font-medium text-text-primary">{platform.name}</span>
                        </div>
                        <Button
                          size="sm"
                          variant={isConnected ? "outline" : "default"}
                          className={!isConnected ? "bg-emerald-brand hover:bg-emerald-hover" : ""}
                          onClick={() => updateData('connected_accounts', platform.id, !isConnected)}
                        >
                          {isConnected ? 'Connected' : 'Connect'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 4: Goals & Strategy */}
            {currentStep === 4 && (
              <div className="space-y-4">
                <h2 className="text-xl font-heading font-semibold text-text-primary mb-4">Goals & Strategy</h2>
                <div>
                  <Label className="mb-3 block">Primary Marketing Goals</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {goals.map((goal) => (
                      <div
                        key={goal}
                        className={cn(
                          "flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all",
                          data.goals_strategy.goals.includes(goal)
                            ? "border-emerald-brand bg-emerald-brand/5"
                            : "border-gray-border hover:border-gray-300"
                        )}
                        onClick={() => toggleGoal(goal)}
                      >
                        <Checkbox checked={data.goals_strategy.goals.includes(goal)} />
                        <span className="text-sm">{goal}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <Label htmlFor="budget">Monthly Marketing Budget</Label>
                  <Input
                    id="budget"
                    value={data.goals_strategy.budget_range}
                    onChange={(e) => updateData('goals_strategy', 'budget_range', e.target.value)}
                    placeholder="e.g., $1,000 - $5,000"
                    className="mt-2"
                  />
                </div>
              </div>
            )}

            {/* Step 5: Review & Launch */}
            {currentStep === 5 && (
              <div className="space-y-4">
                <h2 className="text-xl font-heading font-semibold text-text-primary mb-4">Review & Launch</h2>
                <div className="space-y-4">
                  <div className="p-4 bg-off-white rounded-lg">
                    <h3 className="font-medium text-text-primary mb-2">Business Profile</h3>
                    <p className="text-sm text-gray-medium">{data.business_profile.business_name || 'Not set'}</p>
                    <p className="text-sm text-gray-medium">{data.business_profile.website_url || 'No website'}</p>
                  </div>
                  <div className="p-4 bg-off-white rounded-lg">
                    <h3 className="font-medium text-text-primary mb-2">Target Audience</h3>
                    <p className="text-sm text-gray-medium">{data.target_audience.location || 'Not set'}</p>
                  </div>
                  <div className="p-4 bg-off-white rounded-lg">
                    <h3 className="font-medium text-text-primary mb-2">Connected Accounts</h3>
                    <p className="text-sm text-gray-medium">
                      {Object.entries(data.connected_accounts).filter(([_, v]) => v).length} platforms connected
                    </p>
                  </div>
                  <div className="p-4 bg-off-white rounded-lg">
                    <h3 className="font-medium text-text-primary mb-2">Goals</h3>
                    <p className="text-sm text-gray-medium">
                      {data.goals_strategy.goals.length > 0 ? data.goals_strategy.goals.join(', ') : 'No goals selected'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t border-gray-border">
              <Button
                variant="outline"
                onClick={currentStep === 1 ? handleSkip : handleBack}
                disabled={loading}
              >
                {currentStep === 1 ? (
                  'Skip All'
                ) : (
                  <>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </>
                )}
              </Button>
              {currentStep < 5 ? (
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={handleSkip}>Skip</Button>
                  <Button onClick={handleNext} className="bg-emerald-brand hover:bg-emerald-hover">
                    Next
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              ) : (
                <Button 
                  onClick={handleComplete} 
                  className="bg-emerald-brand hover:bg-emerald-hover"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Launching...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Activate My Marketing
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OnboardingPage;
